import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractErr } from "@/lib/api";
import { Gift, Copy, Check, Users, Crown, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";

// Membership-aligned referral program: rewards are in PRO DAYS, not credits.
// • Referrer: +7 days of Pro per successful invite (stacks)
// • Referee (new signup): +7 days bonus on top of the 7-day auto-trial → 14 days
export default function Referrals() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/me")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(extractErr(e, t("ref_load_error"))))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fullUrl = data ? `${window.location.origin}${data.share_url}` : "";
  const rewardDays = data?.reward_per_referral_days ?? 7;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success(t("ref_link_copied"));
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error(t("ref_copy_error"));
    }
  };

  const shareNative = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({
        title: "Lumioos — AI Salesforce Assistant",
        text: t("ref_share_message"),
        url: fullUrl,
      });
    } catch { /* user cancelled */ }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="referrals-page">
      <Link to="/app/chat" className="inline-flex items-center text-sm text-zinc-500 hover:text-zinc-900 mb-4">
        <ChevronLeft className="w-4 h-4" /> {t("ref_back")}
      </Link>
      <div className="rounded-2xl p-8 text-white shadow-xl" style={{ background: "linear-gradient(135deg,#7c3aed,#db2777)" }}>
        <Gift className="w-12 h-12 mb-3" />
        <h1 className="text-4xl font-black tracking-tight">{t("ref_title")}</h1>
        <p className="mt-2 text-white/90 max-w-xl">
          {t("ref_subtitle", { days: rewardDays })}
        </p>
      </div>

      {loading ? (
        <div className="mt-6 text-center text-zinc-500">{t("ref_loading")}</div>
      ) : data && (
        <>
          {/* Stats — now in Pro days, not credits */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-white border rounded-lg p-5">
              <Users className="w-5 h-5 text-violet-600 mb-2" />
              <div className="text-3xl font-black tracking-tight" data-testid="ref-count">{data.referral_count}</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{t("ref_invited")}</div>
            </div>
            <div className="bg-white border rounded-lg p-5">
              <Crown className="w-5 h-5 text-amber-600 mb-2" />
              <div className="text-3xl font-black tracking-tight" data-testid="ref-days">{data.total_days_earned ?? 0}</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{t("ref_days_earned")}</div>
            </div>
          </div>

          {/* Share link */}
          <div className="mt-6 bg-white border rounded-lg p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("ref_your_link")}</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 font-mono text-sm bg-zinc-50 border rounded px-3 py-2 truncate" data-testid="ref-link">{fullUrl}</div>
              <button onClick={copy} data-testid="ref-copy-btn" className="px-3 py-2 rounded bg-zinc-900 text-white text-xs font-bold inline-flex items-center gap-1">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? t("ref_copied") : t("ref_copy")}
              </button>
              <button onClick={shareNative} className="px-3 py-2 rounded bg-violet-600 text-white text-xs font-bold">{t("ref_share")}</button>
            </div>
            <div className="mt-3 text-[11px] text-zinc-500">
              {t("ref_your_code")} <code className="font-mono text-zinc-900 font-bold">{data.code}</code>
            </div>
          </div>

          {/* Recent — show days awarded per invite */}
          {data.recent?.length > 0 && (
            <div className="mt-6 bg-white border rounded-lg p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">{t("ref_recent")}</div>
              <div className="space-y-2">
                {data.recent.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b last:border-0 pb-2 last:pb-0">
                    <span className="text-zinc-700 font-mono">{r.referee_email}</span>
                    <span className="text-emerald-600 font-bold">{t("ref_days_awarded", { days: r.days_awarded ?? 7 })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggestions */}
          <div className="mt-6 bg-violet-50 border-2 border-violet-200 rounded-lg p-5">
            <div className="font-bold text-violet-900 mb-2">{t("ref_how_to_share")}</div>
            <ul className="text-sm text-violet-900 space-y-1.5 list-disc list-inside">
              <li>{t("ref_share_whatsapp")}</li>
              <li>{t("ref_share_twitter")}</li>
              <li>{t("ref_share_linkedin")}</li>
              <li>{t("ref_share_forum")}</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
